/**
 * Cassette Label Printer Service
 *
 * Sends cassette label print commands via the Bridge system.
 * The bridge desktop app receives the command and forwards the label
 * content to the configured PrintMate AS / LabWriter printer.
 *
 * Label content format (unchanged from legacy):
 *   H6;{caseNumber};{specimenLetter};{blockNumber};{patientName};{facilityName}
 */

import type { CassetteLabel } from "../components/accessioning/CassetteLabelPreview";
import { bridgeService } from "./bridgeService";
import { deviceBridgeService } from "./deviceBridge.service";
import { workstationPreferenceService } from "./workstationPreference.service";

const CASSETTE_DEVICE_KEY = "cassette-printer-device-id";
const CASSETTE_DEVICE_NAME_KEY = "cassette-printer-device-name";

export type CassettePrinterStatus =
  | "folder-selected" // bridge device configured (kept for backward compat)
  | "no-folder" // no bridge device selected (kept for backward compat)
  | "unsupported"; // bridge not reachable

export interface CassettePrintResult {
  success: boolean;
  method: "bridge";
  count: number;
  error?: string;
}

function buildFileContent(label: CassetteLabel): string {
  return `H6;${label.caseNumber};${label.specimenLetter};${label.blockNumber};${label.patientName};${label.facilityName}`;
}

class CassetteLabelPrinterService {
  private deviceId: string | null = null;

  private deviceName: string | null = null;

  private currentStatus: CassettePrinterStatus = "no-folder";

  private statusListeners: Set<(status: CassettePrinterStatus) => void> =
    new Set();

  constructor() {
    this.restoreDevice();
    workstationPreferenceService.onChange(() => this.notifyStatusChange());
  }

  private restoreDevice(): void {
    try {
      const selectedDeviceId =
        workstationPreferenceService.getSelectedDeviceId("cassette_printer");
      const selectedDeviceName =
        workstationPreferenceService.getSelectedDeviceName("cassette_printer");
      if (selectedDeviceId) {
        this.deviceId = selectedDeviceId;
        this.deviceName = selectedDeviceName;
        this.currentStatus = "folder-selected";
        return;
      }

      const storedId = localStorage.getItem(CASSETTE_DEVICE_KEY);
      const storedName = localStorage.getItem(CASSETTE_DEVICE_NAME_KEY);
      if (storedId) {
        this.deviceId = storedId;
        this.deviceName = storedName;
        this.currentStatus = "folder-selected";
      }
    } catch {
      // localStorage unavailable; ignore
    }
  }

  get status(): CassettePrinterStatus {
    if (
      workstationPreferenceService.hasInvalidPreferredPrinter(
        "cassette_printer"
      )
    ) {
      return "no-folder";
    }
    if (workstationPreferenceService.getPreferredPrinter("cassette_printer")) {
      return "folder-selected";
    }
    return this.currentStatus;
  }

  /**
   * Returns the configured bridge device name (replaces the old folder name).
   */
  get folderName(): string | null {
    return (
      workstationPreferenceService.getPreferredPrinter("cassette_printer")
        ?.deviceName ?? this.deviceName
    );
  }

  get isFolderSelected(): boolean {
    return this.status === "folder-selected";
  }

  /**
   * Returns the configured bridge device ID for external use.
   */
  get selectedDeviceId(): string | null {
    return (
      workstationPreferenceService.getPreferredPrinter("cassette_printer")
        ?.deviceId ?? this.deviceId
    );
  }

  onStatusChange(
    listener: (status: CassettePrinterStatus) => void
  ): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private notifyStatusChange(): void {
    this.statusListeners.forEach((listener) => listener(this.status));
  }

  /**
   * Configure the bridge device to use for cassette printing.
   * Replaces the old selectOutputDirectory() folder picker.
   */
  setDevice(deviceId: string, deviceName: string): void {
    this.deviceId = deviceId;
    this.deviceName = deviceName;
    this.currentStatus = "folder-selected";
    try {
      localStorage.setItem(CASSETTE_DEVICE_KEY, deviceId);
      localStorage.setItem(CASSETTE_DEVICE_NAME_KEY, deviceName);
    } catch {
      // localStorage unavailable; ignore
    }
    this.notifyStatusChange();
  }

  /**
   * @deprecated Use setDevice() instead. Kept for backward compatibility.
   * Now auto-detects the first available cassette_printer from the bridge.
   */
  async selectOutputDirectory(): Promise<boolean> {
    try {
      const workstationsRes = await bridgeService.getWorkstations({
        status: "online",
      });
      const workstations = workstationsRes.data.data;
      if (workstations.length === 0) return false;

      for (let i = 0; i < workstations.length; i += 1) {
        const ws = workstations[i];
        // eslint-disable-next-line no-await-in-loop
        const devicesRes = await bridgeService.getDevices(ws.id, {
          deviceType: "cassette_printer",
        });
        const devices = devicesRes.data.data;
        if (devices.length > 0) {
          this.setDevice(devices[0].id, devices[0].name);
          return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Send cassette labels to the bridge for printing.
   */
  async printCassetteLabels(
    labels: CassetteLabel[],
    explicitDeviceId?: string
  ): Promise<CassettePrintResult> {
    if (labels.length === 0) {
      return {
        success: false,
        method: "bridge",
        count: 0,
        error: "No labels to print",
      };
    }

    const deviceId = explicitDeviceId ?? this.getEffectiveDeviceId();
    if (!deviceId) {
      return {
        success: false,
        method: "bridge",
        count: 0,
        error:
          "No printer device configured. Please connect the Bridge app and select a printer.",
      };
    }
    const labelContents = labels.map((label) => ({
      content: buildFileContent(label),
      labelType: "cassette" as const,
      copies: 1,
    }));

    // ── Path 1: Local bridge (fast, no backend round-trip) ──
    const requiresBackend =
      !!explicitDeviceId ||
      workstationPreferenceService.shouldUseBackendForDevice(
        "cassette_printer",
        deviceId
      );
    if (deviceBridgeService.isLocal() && !requiresBackend) {
      try {
        const result = await deviceBridgeService.sendCommand(
          {
            type: "PRINT_CASSETTE_LABELS",
            payload: { labels: labelContents, deviceId },
          },
          30000
        );
        const payload = result.payload ?? {};
        if (payload.success) {
          return {
            success: true,
            method: "bridge" as const,
            count: labels.length,
          };
        }
        const localError =
          (payload.error as string) || "Print failed via local bridge";
        if (this.isDeviceNotFoundError(localError)) {
          this.clearStaleDevice(deviceId);
        }
        return {
          success: false,
          method: "bridge" as const,
          count: 0,
          error: localError,
        };
      } catch {
        // Local bridge failed — fall through to backend path
      }
    }

    // ── Path 2: Backend relay (fallback) ──
    try {
      await bridgeService.createCommand({
        deviceId,
        commandType: "print_cassette_labels",
        payload: { labels: labelContents },
        expiresInSeconds: 60,
      });

      return {
        success: true,
        method: "bridge",
        count: labels.length,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        (error instanceof Error ? error.message : "Failed to send to bridge");
      if (this.isDeviceNotFoundError(errorMessage)) {
        this.clearStaleDevice(deviceId);
      }
      return {
        success: false,
        method: "bridge",
        count: 0,
        error: errorMessage,
      };
    }
  }

  getStatusMessage(): string {
    switch (this.currentStatus) {
      case "folder-selected":
        return `Bridge printer: ${this.folderName ?? "Configured"}`;
      case "no-folder":
        return "No printer configured — connect Bridge app";
      case "unsupported":
        return "Bridge connection not available";
      default:
        return "Unknown status";
    }
  }

  disconnect(): void {
    this.deviceId = null;
    this.deviceName = null;
    this.currentStatus = "no-folder";
    this.notifyStatusChange();
    try {
      localStorage.removeItem(CASSETTE_DEVICE_KEY);
      localStorage.removeItem(CASSETTE_DEVICE_NAME_KEY);
    } catch {
      // localStorage unavailable; ignore
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private isDeviceNotFoundError(msg: string): boolean {
    return /device.*not found|not found.*device/i.test(msg);
  }

  private getEffectiveDeviceId(): string | null {
    if (
      workstationPreferenceService.hasInvalidPreferredPrinter(
        "cassette_printer"
      )
    ) {
      return null;
    }
    const preferredPrinter =
      workstationPreferenceService.getPreferredPrinter("cassette_printer");
    return (
      preferredPrinter?.deviceId ??
      workstationPreferenceService.getSelectedDeviceId("cassette_printer") ??
      this.deviceId
    );
  }

  private clearStaleDevice(failedDeviceId: string): void {
    const preferredPrinter =
      workstationPreferenceService.getPreferredPrinter("cassette_printer");
    if (preferredPrinter?.deviceId === failedDeviceId) {
      return;
    }
    this.deviceId = null;
    this.deviceName = null;
    this.currentStatus = "no-folder";
    try {
      localStorage.removeItem(CASSETTE_DEVICE_KEY);
      localStorage.removeItem(CASSETTE_DEVICE_NAME_KEY);
      localStorage.removeItem("bridge-device-cassette_printer-id");
      localStorage.removeItem("bridge-device-cassette_printer-name");
    } catch {
      // ignore
    }
    this.notifyStatusChange();
  }
}

export const cassetteLabelPrinterService = new CassetteLabelPrinterService();
